import { motion } from "framer-motion";
import {
  AlertTriangle,
  Archive,
  Building2,
  CalendarCheck,
  CalendarX,
  Calculator,
  Copy,
  Download,
  Link2,
  ListChecks,
  Mail,
  Play,
  RefreshCcw,
  Wallet,
  Receipt
} from "lucide-react";
import { useState } from "react";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { ConfirmDialog } from "../../../../components/ui/confirm-dialog";
import { DatePicker } from "../../../../components/ui/date-picker";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { Badge } from "../../../../components/ui/badge";
import { format } from "date-fns";
import { useToast } from "../../../../hooks/use-toast";
import { CompletudePanel } from "./components/completude-panel";
import {
  useBuscarLinksZips,
  useCalcularSaldoRemanescente,
  useCompletudeNfs,
  useDispararDownloadNfs,
  useEnviarEmailFechamento,
  useEnviarEmailSaldo,
  useFechamentoStatus,
  useGerarZipsNfs,
  useProcessarNfsSaldo,
  useResumoNfs,
} from "./hooks/use-controle-nfs";

import { ZipLinksResult } from "./types";
import { agruparPorFamilia, familiaTemLinkPronto, textoDaFamilia } from "./utils/familias";

// Segunda-feira da última semana fechada (segunda da semana anterior)
const getPreviousMonday = (): Date => {
  const now = new Date();
  const daysSinceMonday = (now.getDay() + 6) % 7;
  const prevMonday = new Date(now);
  prevMonday.setDate(now.getDate() - daysSinceMonday - 7);
  prevMonday.setHours(0, 0, 0, 0);
  return prevMonday;
};

export default function ControleNFSPage() {
  const [activeTab, setActiveTab] = useState("fechamento");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(getPreviousMonday());
  const [zipLinks, setZipLinks] = useState<ZipLinksResult | null>(null);
  const [completudeAberta, setCompletudeAberta] = useState(false);
  const { toast } = useToast();
  
  // Dialogs
  const [confirmEmailFechamento, setConfirmEmailFechamento] = useState(false);
  const [enviarParaContato, setEnviarParaContato] = useState(false);
  const [confirmSaldoNfs, setConfirmSaldoNfs] = useState(false);

  // Queries & Mutations
  const { data: fechamentoStatus, isLoading: loadingFechamento, refetch: refetchFechamento } = useFechamentoStatus();
  
  const minStartDateStr = selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined;
  const { data: resumoNfs, isLoading: loadingResumoNfs } = useResumoNfs(minStartDateStr);

  const enviarEmailFechamentoMutation = useEnviarEmailFechamento();
  const calcularSaldoMutation = useCalcularSaldoRemanescente();
  const processarNfsSaldoMutation = useProcessarNfsSaldo();
  const enviarEmailSaldoMutation = useEnviarEmailSaldo();
  const dispararDownloadMutation = useDispararDownloadNfs();
  const gerarZipsMutation = useGerarZipsNfs();
  const buscarLinksMutation = useBuscarLinksZips();
  const completude = useCompletudeNfs(minStartDateStr, completudeAberta);

  const handleBuscarLinks = () => {
    if (!minStartDateStr) return;
    buscarLinksMutation.mutate(
      { segunda: minStartDateStr },
      { onSuccess: (response) => setZipLinks(response.data ?? null) }
    );
  };

  const formatExpiry = (iso: string) =>
    new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const buildLinksText = (result: ZipLinksResult) => {
    return agruparPorFamilia(result.links)
      .filter(familiaTemLinkPronto)
      .map((familia) => textoDaFamilia(familia, result.periodo, formatExpiry(result.expiresAt), formatDatePTBR))
      .join("\n\n————————————\n\n");
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast({ title: "Copiado", description: label }))
      .catch(() => toast({ title: "Erro", description: "Não foi possível copiar.", variant: "destructive" }));
  };

  const handleConfirmEmailFechamento = () => {
    enviarEmailFechamentoMutation.mutate(
      { enviar_email: true, enviar_para_contato: enviarParaContato },
      { onSuccess: () => setConfirmEmailFechamento(false) }
    );
  };

  const handleConfirmProcessarNfs = () => {
    if (!minStartDateStr) return;
    processarNfsSaldoMutation.mutate(
      { data_inicio_periodo: minStartDateStr, ops: "entrego" },
      { onSuccess: () => setConfirmSaldoNfs(false) }
    );
  };

  const formatDatePTBR = (dateStr?: string) => {
    if (!dateStr) return "—";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const renderFechamentoStatus = () => {
    if (loadingFechamento && !fechamentoStatus) {
      return (
        <Card className="mb-6 flex justify-center items-center py-12">
          <RefreshCcw className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Verificando status da semana...</span>
        </Card>
      );
    }

    if (!fechamentoStatus) {
      return (
        <Card className="mb-6 flex justify-center py-6">
          <Button variant="outline" onClick={() => refetchFechamento()}>
            <RefreshCcw className="mr-2 h-4 w-4" />
            Verificar Status
          </Button>
        </Card>
      );
    }

    const { semana_completa, periodo, dias_faltantes } = fechamentoStatus;
    const periodoStr = periodo ? `${formatDatePTBR(periodo.segunda)} – ${formatDatePTBR(periodo.domingo)}` : "";

    if (semana_completa) {
      return (
        <Card className="mb-6 border-green-500 bg-green-50/50 dark:bg-green-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <CalendarCheck className="h-10 w-10 text-green-600 dark:text-green-500" />
                <div>
                  <h3 className="text-xl font-bold text-green-700 dark:text-green-400">Semana Completa</h3>
                  {periodoStr && <p className="text-sm text-muted-foreground">{periodoStr}</p>}
                </div>
              </div>
              <Badge variant="outline" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-200">
                ✓ Fechado
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Todos os dias da semana anterior possuem registros comprados ou liquidados para todas as operações.
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="mb-6 border-amber-500 bg-amber-50/50 dark:bg-amber-950/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CalendarX className="h-10 w-10 text-amber-600 dark:text-amber-500" />
              <div>
                <h3 className="text-xl font-bold text-amber-700 dark:text-amber-400">Semana Incompleta</h3>
                {periodoStr && <p className="text-sm text-muted-foreground">{periodoStr}</p>}
              </div>
            </div>
            <Badge variant="outline" className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 border-amber-200">
              Pendente
            </Badge>
          </div>
          
          {dias_faltantes && dias_faltantes.length > 0 && (
            <div className="mt-4 border rounded-md overflow-hidden bg-background">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-amber-700 dark:text-amber-400 w-48">Data Romaneio</th>
                    <th className="px-4 py-2 text-left font-semibold text-amber-700 dark:text-amber-400">OPS faltantes</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {Object.entries(
                    dias_faltantes.reduce((acc: any, item: any) => {
                      if (!acc[item.date]) acc[item.date] = [];
                      acc[item.date].push(item.ops);
                      return acc;
                    }, {})
                  )
                  .sort(([dateA], [dateB]) => String(dateA).localeCompare(String(dateB)))
                  .map(([date, ops]: any) => (
                    <tr key={date}>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2 font-medium">
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                          {formatDatePTBR(date)}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {ops.map((op: string) => (
                            <Badge key={op} variant="outline" className="text-xs uppercase border-amber-200 text-amber-700 dark:text-amber-400">
                              {op}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6">
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center gap-3">
          <motion.div
            className="p-2 rounded-lg bg-primary/10"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <Receipt className="h-6 w-6 text-primary" />
          </motion.div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Fechamento Entrego</h1>
            <p className="text-muted-foreground mt-1">Fechamento semanal financeiro e gestão de saldo remanescente</p>
          </div>
        </div>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="fechamento" className="flex items-center gap-2">
            <CalendarCheck className="h-4 w-4" />
            Fechamento Semanal
          </TabsTrigger>
          <TabsTrigger value="saldo" className="flex items-center gap-2">
            <Wallet className="h-4 w-4" />
            Saldo Remanescente
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fechamento" className="space-y-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {renderFechamentoStatus()}

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Ações de Fechamento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => refetchFechamento()}
                    disabled={loadingFechamento}
                  >
                    <RefreshCcw className={`mr-2 h-4 w-4 ${loadingFechamento ? "animate-spin" : ""}`} />
                    {loadingFechamento ? "Verificando..." : "Verificar Status da Semana"}
                  </Button>
                  
                  <Button
                    className="w-full h-12"
                    onClick={() => setConfirmEmailFechamento(true)}
                    disabled={enviarEmailFechamentoMutation.isPending || loadingFechamento}
                  >
                    {enviarEmailFechamentoMutation.isPending ? (
                      <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="mr-2 h-4 w-4" />
                    )}
                    {enviarEmailFechamentoMutation.isPending ? "Enviando..." : "Enviar Email de Fechamento"}
                  </Button>

                  <Button
                    variant="secondary"
                    className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => calcularSaldoMutation.mutate()}
                    disabled={calcularSaldoMutation.isPending}
                  >
                    {calcularSaldoMutation.isPending ? (
                      <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Calculator className="mr-2 h-4 w-4" />
                    )}
                    {calcularSaldoMutation.isPending ? "Calculando..." : "Calcular Saldo Remanescente"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="saldo" className="space-y-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="mb-6">
              <CardHeader className="flex flex-row items-center gap-2">
                <CardTitle className="text-xl">Resumo Saldo Remanescente — aptos a emissão de NFS</CardTitle>
                {loadingResumoNfs && <RefreshCcw className="h-4 w-4 animate-spin text-muted-foreground ml-2" />}
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Somente saldos de cadastros com tipo de emissão <strong>en</strong>, em OPS com emissão de NFS ou de
                  Saldo Remanescente habilitada.
                </p>
                {!minStartDateStr ? (
                  <p className="text-muted-foreground">Selecione uma data de início do período para ver o resumo.</p>
                ) : !resumoNfs && !loadingResumoNfs ? (
                  <p className="text-muted-foreground">Nenhum dado encontrado para o período selecionado.</p>
                ) : resumoNfs ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="rounded-lg border bg-card p-4 text-center">
                      <div className="text-4xl font-bold">{resumoNfs.resumoTotal?.totalRegistros || 0}</div>
                      <div className="text-sm text-muted-foreground mt-1">Total Registros</div>
                    </div>
                    <div className="rounded-lg border border-green-200 bg-green-50/50 dark:bg-green-900/20 p-4 text-center">
                      <div className="text-4xl font-bold text-green-600 dark:text-green-500">
                        {resumoNfs.resumoTotal?.totalComNfs || 0}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">Com NFS</div>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50/50 dark:bg-amber-900/20 p-4 text-center">
                      <div className="text-4xl font-bold text-amber-600 dark:text-amber-500">
                        {resumoNfs.resumoTotal?.totalSemNfs || 0}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">Sem NFS</div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Solicitar NFS de Saldo Remanescente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                  <div className="space-y-2">
                    <Label>Data Início Período</Label>
                    <DatePicker
                      date={selectedDate}
                      setDate={setSelectedDate}
                      placeholder="Selecione a data"
                    />
                  </div>
                  
                  <div className="p-3 rounded-md border bg-muted/50 flex items-center gap-3 h-10">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-col justify-center">
                      <span className="text-sm font-medium leading-none">Todas as ops com NFS ativo</span>
                      <span className="text-[10px] text-muted-foreground mt-1">Buscado automaticamente (entrego)</span>
                    </div>
                  </div>

                  <Button
                    className="w-full h-10"
                    disabled={processarNfsSaldoMutation.isPending || !selectedDate}
                    onClick={() => setConfirmSaldoNfs(true)}
                  >
                    {processarNfsSaldoMutation.isPending ? (
                      <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="mr-2 h-4 w-4" />
                    )}
                    {processarNfsSaldoMutation.isPending ? "Processando..." : "Processar NFS"}
                  </Button>
                </div>

                <div className="mt-8 flex justify-end">
                  <Button
                    variant="secondary"
                    className="bg-green-600 hover:bg-green-700 text-white min-w-[280px]"
                    onClick={() => {
                      if (!minStartDateStr) return;
                      enviarEmailSaldoMutation.mutate({ data_inicio_periodo: minStartDateStr });
                    }}
                    disabled={enviarEmailSaldoMutation.isPending || !selectedDate}
                  >
                    {enviarEmailSaldoMutation.isPending ? (
                      <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="mr-2 h-4 w-4" />
                    )}
                    {enviarEmailSaldoMutation.isPending ? "Enviando..." : "Enviar Email de Saldo Remanescente"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-xl">Download de NFS e ZIPs por Franquia</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Usa a mesma <strong>Data Início Período</strong> selecionada acima
                  {minStartDateStr ? ` (semana de ${formatDatePTBR(minStartDateStr)})` : ""}.
                  Primeiro dispare o download das NFS, depois gere os ZIPs e busque os links.
                  Buscar links também gera os CSVs de Saldo Remanescente (os mesmos do email) com link de download.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Button
                    variant="outline"
                    className="w-full h-12"
                    disabled={dispararDownloadMutation.isPending || !minStartDateStr}
                    onClick={() => dispararDownloadMutation.mutate({ segunda: minStartDateStr! })}
                  >
                    {dispararDownloadMutation.isPending ? (
                      <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    {dispararDownloadMutation.isPending ? "Disparando..." : "Baixar NFS da Semana (adhoc)"}
                  </Button>

                  <Button
                    className="w-full h-12"
                    disabled={gerarZipsMutation.isPending || !minStartDateStr}
                    onClick={() => gerarZipsMutation.mutate({ segunda: minStartDateStr! })}
                  >
                    {gerarZipsMutation.isPending ? (
                      <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Archive className="mr-2 h-4 w-4" />
                    )}
                    {gerarZipsMutation.isPending ? "Enfileirando..." : "Gerar ZIPs por Franquia"}
                  </Button>

                  <Button
                    variant="secondary"
                    className="w-full h-12"
                    disabled={buscarLinksMutation.isPending || !minStartDateStr}
                    onClick={handleBuscarLinks}
                  >
                    {buscarLinksMutation.isPending ? (
                      <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Link2 className="mr-2 h-4 w-4" />
                    )}
                    {buscarLinksMutation.isPending ? "Buscando..." : "Buscar Links dos ZIPs"}
                  </Button>

                  <Button
                    variant={completudeAberta ? "secondary" : "outline"}
                    className="w-full h-12"
                    disabled={!minStartDateStr}
                    onClick={() => setCompletudeAberta((v) => !v)}
                  >
                    <ListChecks className="mr-2 h-4 w-4" />
                    {completudeAberta ? "Ocultar Completude" : "Ver Completude das Notas"}
                  </Button>
                </div>

                {completudeAberta && (
                  <CompletudePanel
                    data={completude.data}
                    isLoading={completude.isLoading}
                    isFetching={completude.isFetching}
                    isError={completude.isError}
                    onRefresh={() => completude.refetch()}
                  />
                )}

                {zipLinks && (
                  <div className="mt-6 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm">
                        Semana <strong>{formatDatePTBR(zipLinks.periodo.segunda)}</strong> a{" "}
                        <strong>{formatDatePTBR(zipLinks.periodo.domingo)}</strong> — links válidos até{" "}
                        <strong>{formatExpiry(zipLinks.expiresAt)}</strong> (7 dias)
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!zipLinks.links.some((l) => (l.ready && l.url) || l.csvUrl)}
                        onClick={() => copyToClipboard(buildLinksText(zipLinks), "Todos os links copiados.")}
                      >
                        <Copy className="mr-2 h-3.5 w-3.5" />
                        Copiar todos
                      </Button>
                    </div>

                    {agruparPorFamilia(zipLinks.links).map((familia) => (
                      <div key={familia.nome} className="border rounded-md bg-muted/20 overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b bg-muted/40">
                          <span className="font-bold uppercase">{familia.nome}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!familiaTemLinkPronto(familia)}
                            onClick={() =>
                              copyToClipboard(
                                textoDaFamilia(familia, zipLinks.periodo, formatExpiry(zipLinks.expiresAt), formatDatePTBR),
                                `Texto da família ${familia.nome.toUpperCase()} copiado.`
                              )
                            }
                          >
                            <Copy className="mr-2 h-3.5 w-3.5" />
                            Copiar texto da família
                          </Button>
                        </div>
                        <p className="px-3 py-2 text-xs text-muted-foreground border-b">
                          Links válidos até {formatExpiry(zipLinks.expiresAt)} (7 dias após a geração). O ZIP contém os
                          PDFs das NFS da semana; o CSV traz o detalhamento do Saldo Remanescente apto a emissão.
                        </p>
                        <div className="divide-y">
                          {familia.links.map((link) => (
                            <div key={link.ops} className="px-3 py-2 text-sm space-y-1.5">
                              <div className="flex items-center gap-2">
                                <span className="font-bold uppercase w-44 truncate shrink-0">{link.ops}</span>
                                {link.faltantes != null && link.faltantes > 0 && (
                                  <Badge variant="outline" className="text-amber-600 border-amber-200 shrink-0">
                                    {link.faltantes} faltante(s)
                                  </Badge>
                                )}
                                {link.csvRegistros != null && (
                                  <Badge variant="secondary" className="shrink-0">
                                    {link.csvRegistros} saldo(s) no CSV
                                  </Badge>
                                )}
                              </div>
                              {link.ready && link.url ? (
                                <div className="flex items-center gap-2">
                                  <span className="w-14 shrink-0 text-xs font-semibold text-muted-foreground">ZIP</span>
                                  <a
                                    href={link.url}
                                    className="truncate flex-1 font-mono text-xs text-primary underline-offset-2 hover:underline select-all"
                                  >
                                    {link.url}
                                  </a>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="shrink-0 h-7 w-7"
                                    title={`Copiar link do ZIP de ${link.ops}`}
                                    onClick={() => copyToClipboard(link.url!, `Link do ZIP de ${link.ops} copiado.`)}
                                  >
                                    <Copy className="h-4 w-4 text-primary" />
                                  </Button>
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">
                                  ZIP ainda não pronto — gere os ZIPs e busque novamente em instantes.
                                </p>
                              )}
                              {link.csvUrl && (
                                <div className="flex items-center gap-2">
                                  <span className="w-14 shrink-0 text-xs font-semibold text-muted-foreground">CSV SR</span>
                                  <a
                                    href={link.csvUrl}
                                    className="truncate flex-1 font-mono text-xs text-primary underline-offset-2 hover:underline select-all"
                                  >
                                    {link.csvUrl}
                                  </a>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="shrink-0 h-7 w-7"
                                    title={`Copiar link do CSV de ${link.ops}`}
                                    onClick={() => copyToClipboard(link.csvUrl!, `Link do CSV de ${link.ops} copiado.`)}
                                  >
                                    <Copy className="h-4 w-4 text-primary" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {zipLinks.links.length === 0 && (
                      <div className="border rounded-md px-3 py-4 text-sm text-muted-foreground">
                        Nenhuma franquia com notas na semana selecionada.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Dialog: Confirmar Email Fechamento */}
      <ConfirmDialog
        open={confirmEmailFechamento}
        onOpenChange={setConfirmEmailFechamento}
        onConfirm={handleConfirmEmailFechamento}
        title="Confirmar Envio de Email — Fechamento Semanal"
        confirmText={enviarEmailFechamentoMutation.isPending ? "Enviando..." : "Confirmar Envio"}
        cancelText="Cancelar"
      >
        <div className="space-y-4">
          <p className="text-sm">Você está prestes a enviar o email de fechamento financeiro semanal do Entrego.</p>
          <div className="bg-muted p-3 rounded-lg text-sm space-y-2 border">
            <div><strong>Período:</strong> Semana anterior (segunda → domingo)</div>
            <div>
              <strong>Destinatários:</strong>{" "}
              {enviarParaContato ? "Contato externo da franquia + backoffice (CC)" : "Apenas backoffice interno"}
            </div>
          </div>
          <div className="flex items-center space-x-2 py-2">
            <Switch
              id="enviar-contato"
              checked={enviarParaContato}
              onCheckedChange={setEnviarParaContato}
            />
            <Label htmlFor="enviar-contato">Enviar também para contato externo da franquia</Label>
          </div>
          <div className="text-amber-600 dark:text-amber-500 text-sm font-medium flex items-start gap-2 mt-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              Esta ação irá disparar emails para a semana de{" "}
              <span className="underline">{fechamentoStatus?.periodo?.segunda ? formatDatePTBR(fechamentoStatus.periodo.segunda) : "—"}</span>
              {" "}até{" "}
              <span className="underline">{fechamentoStatus?.periodo?.domingo ? formatDatePTBR(fechamentoStatus.periodo.domingo) : "—"}</span>
              . Verifique o período antes de confirmar.
            </p>
          </div>
        </div>
      </ConfirmDialog>

      {/* Dialog: Confirmar Processamento NFS Saldo Remanescente */}
      <ConfirmDialog
        open={confirmSaldoNfs}
        onOpenChange={setConfirmSaldoNfs}
        onConfirm={handleConfirmProcessarNfs}
        title="Confirmar Processamento NFS — Saldo Remanescente"
        confirmText="Confirmar Processamento"
        cancelText="Cancelar"
      >
        <div className="space-y-4">
          <p className="text-sm">Você está prestes a processar NFS para saldo remanescente com os seguintes parâmetros:</p>
          <div className="bg-muted p-3 rounded-lg text-sm space-y-2 border">
            <div><strong>Data Início:</strong> {minStartDateStr ? formatDatePTBR(minStartDateStr) : "—"}</div>
            <div><strong>Operação:</strong> Todas as ops com NFS ativo (entrego)</div>
          </div>
          <div className="text-amber-600 dark:text-amber-500 text-sm font-medium flex items-center gap-2 mt-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <p>Esta operação irá processar automaticamente todos os registros encontrados.</p>
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
}
